/**
 * Settings as one screen: the groups down the left, the chosen page beside
 * them.
 *
 * This replaces two full-width sheets -- Settings and Accounts -- that the
 * sidebar switched between. Two sheets meant the same eleven cards were a
 * single scroll on a phone and a single scroll on a 27-inch monitor, and that
 * "Accounts" and "Appearance" looked like two different kinds of thing when
 * they are both settings for the same app.
 *
 * # A page does not know what is to its left
 *
 * The shell renders a group list and a region; a page is a component in that
 * region and nothing more. It is not told which group is current, it does not
 * draw a back button, and it does not know whether the group list is beside it
 * or has been replaced by it. That is what lets {@link Accounts} be a page here
 * while also being two panels of its own -- groups, accounts, account -- and it
 * is why `bare` below is a property of the page rather than a check for the
 * string "accounts" somewhere in the layout.
 *
 * `bare` says the page is supplying its own structure, so the region hands it
 * the whole rectangle: no padding, no heading, no card. Every other page gets
 * the frame -- a scrolling column, a centred measure, and its own name as the
 * heading -- because writing that seven times is seven chances to write it
 * differently.
 *
 * # Which pages there are
 *
 * Seven, out of the twelve cards this screen used to stack, grouped by what
 * someone is trying to change rather than by which module implements it.
 * Sessions and Password go with the identity they belong to; About, the admin
 * panel and the device token are reference material and go together at the
 * bottom rather than sitting in the list as peers of Appearance.
 */

import type { ComponentType } from "preact";
import { useEffect, useRef } from "preact/hooks";

import * as nav from "../lib/navigation";
import * as retention from "../lib/retention";
import { Accounts } from "./Accounts";
import * as Icon from "./Icons";
import {
  About,
  Appearance,
  LowPriority,
  Notifications,
  Offline,
  Privacy,
  Sending,
  SignIn,
} from "./Settings";

type Page = {
  id: nav.SettingsPage;
  /** Names the group in the list, the heading on the page and the pane on the way in. */
  label: string;
  /** The page owns its region entirely: no frame, no heading, no padding. */
  bare?: true;
  body: ComponentType;
};

const PAGES: Page[] = [
  { id: "accounts", label: "Accounts", bare: true, body: Accounts },
  { id: "appearance", label: "Appearance", body: Appearance },
  { id: "sending", label: "Sending", body: Sending },
  { id: "notifications", label: "Notifications", body: Notifications },
  // Directly under Notifications, because the two are one subject read from
  // opposite ends: this page is the list of senders that will never produce
  // one. Somebody arriving at Notifications to ask "why did that buzz" and
  // somebody arriving to ask "how do I make it stop" want the same two entries
  // next to each other.
  { id: "lowpriority", label: "Low priority", body: LowPriority },
  // Only where there is somewhere to keep mail. The browser build has no local
  // store, so the page is left out entirely rather than drawn empty: "no
  // offline storage on this build" and "offline storage, holding nothing" are
  // different sentences, and only the second is worth a screen. The page id
  // stays reserved either way, so a link to it from a desktop build does not
  // become a dead URL in a browser -- `navigation.ts` simply finds no page.
  ...(retention.available
    ? // The id stays `offline`: it is URL surface and somebody has the address.
      // Only the label follows the rename to "Saved".
      [{ id: "offline" as const, label: "Saved mail", body: Offline }]
    : []),
  { id: "privacy", label: "Privacy", body: Privacy },
  { id: "signin", label: "Sign-in and security", body: SignIn },
  { id: "about", label: "About", body: About },
];

export function SettingsShell() {
  const page = nav.settingsPage.value;
  const split = nav.settingsSplit.value;
  const chosen = PAGES.find((p) => p.id === page) ?? null;

  /*
   * The last page chosen stays rendered while the group list is over it.
   *
   * Only reachable on a narrow screen, where the two are one pane taking turns.
   * The region is hidden rather than emptied because a page is not a view of
   * data that can be rebuilt: the password form holds three fields somebody is
   * part-way through typing, and the sessions list holds a request that would
   * be made again. Backing out to the group list and changing your mind must
   * not cost either.
   */
  const held = useRef<Page | null>(null);
  if (chosen) held.current = chosen;
  const rendered = chosen ?? held.current;

  const region = useRef<HTMLElement | null>(null);
  const list = useRef<HTMLElement | null>(null);
  useFocusOnPageChange(page, split, region, list);

  /*
   * What the one-pane layout shows, decided by what can actually be *drawn*
   * rather than by whether an id is set.
   *
   * Those came apart the moment a page became conditional. `offline` is in
   * `navigation.ts`'s allow-list unconditionally -- it has to be, or a link
   * from a desktop build would not parse at all -- but the browser has no entry
   * for it in `PAGES`. Keyed off `page`, that combination said "page", the
   * stylesheet hid the group list to make room for it, and nothing was
   * rendered: a header, a back button and an empty body on a phone-width
   * browser.
   *
   * Keyed off `chosen` it degrades to the group list instead, which is where
   * somebody who followed a link this build cannot honour should end up. Note
   * that this resets nothing -- the id stays in the URL until something else is
   * chosen -- because a state write during render is a worse cure than the
   * disease.
   */
  const showing = split ? "both" : chosen === null ? "nav" : "page";

  return (
    <>
      <div class="col-head" data-tauri-drag-region="deep">
        {/*
          `nav.back()` and not a jump to the mail, so this button and the system
          back button take the same step: on a phone the page is a layer over
          the group list, and a header button that skipped it would leave a
          history entry behind that the next back press would spend going
          nowhere visible.
        */}
        <button
          class="icon-btn"
          aria-label={showing === "page" ? "Back to settings" : "Back to mail"}
          onClick={() => nav.back()}
        >
          <Icon.Back />
        </button>
        {/*
          The head is the heading, on a layout where the page has none of its
          own. Below the split the page's name is here rather than repeated
          inside it -- twice in sixty pixels is not emphasis -- and this line is
          the copy that stays put while the page scrolls under it, so it is the
          one worth marking up as the heading.
        */}
        <strong
          role={split ? undefined : "heading"}
          aria-level={split ? undefined : 1}
        >
          {showing === "page" && chosen ? chosen.label : "Settings"}
        </strong>
      </div>

      <div class="settings-body" data-settings-pane={showing}>
        {/*
          A `nav` with the current group marked, because that is what it is: a
          list of destinations, one of which you are at. `aria-current` is the
          same spelling the mailbox list uses, which is also where the styling
          comes from -- a second set of rules for a list that looks the same
          would be a second set to keep in step.
        */}
        <nav class="settings-nav" aria-label="Settings sections" tabIndex={-1} ref={list}>
          <div class="nav">
            {PAGES.map((p) => (
              <button
                key={p.id}
                class="nav-item"
                aria-current={p.id === page}
                onClick={() => (nav.settingsPage.value = p.id)}
              >
                <span class="label">{p.label}</span>
                {/*
                  Drawn only where pressing this replaces the list with the
                  page. Beside the page it would be pointing at something
                  already on screen, which is how a chevron stops meaning
                  anything. The stylesheet decides; there is no second answer
                  here about how wide the window is.
                */}
                <Icon.Chevron />
              </button>
            ))}
          </div>
        </nav>

        {rendered && (
          <section
            class={rendered.bare ? "settings-page bare" : "settings-page col-body"}
            aria-label={rendered.label}
            tabIndex={-1}
            ref={region}
          >
            {rendered.bare ? (
              <rendered.body />
            ) : (
              <div class="panel">
                {/* Only where the head above says "Settings"; see the head. */}
                {split && <h1>{rendered.label}</h1>}
                <rendered.body />
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
}

/**
 * Put focus where the page went, but only when something left the screen.
 *
 * The same rule `app.tsx` applies to panes, for the same reason. On a narrow
 * screen the group list and the page replace one another, so whatever had focus
 * has just been hidden -- and focus on a hidden element is focus on `<body>`,
 * which costs a screen-reader user their place and starts the next Tab at the
 * top of the document. Moving it to the container that arrived also announces
 * the page's name, which is the sentence worth hearing.
 *
 * Where both columns are on screen it does nothing, deliberately: the group
 * list is still there, focus is on the group that was just pressed, and taking
 * it away would mean a keyboard could never walk down the list.
 */
function useFocusOnPageChange(
  page: nav.SettingsPage | null,
  split: boolean,
  region: { current: HTMLElement | null },
  list: { current: HTMLElement | null },
): void {
  const shown = useRef<nav.SettingsPage | null | undefined>(undefined);
  useEffect(() => {
    const first = shown.current === undefined;
    if (shown.current === page) return;
    shown.current = page;
    // The first render is the pane arriving, not a page changing inside it.
    // `app.tsx` has just moved focus to the settings pane as a whole; taking it
    // again here would announce the page twice and the pane not at all.
    if (first || split) return;
    (page === null ? list.current : region.current)?.focus();
  }, [page, split]);
}
